import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { X, UserX, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { API, setSessionToken } from "@/utils/api";
import { trackSignup, trackLogin } from "@/utils/fbPixel";

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("Initializing...");
  const [noAccountFound, setNoAccountFound] = useState(false);
  const [foundingMemberAvailable, setFoundingMemberAvailable] = useState(true);

  // Check founding member status
  useEffect(() => {
    const checkFoundingMember = async () => {
      try {
        const response = await fetch(`${API}/auth/founding-member-status`);
        const data = await response.json();
        setFoundingMemberAvailable(data.available);
      } catch (err) {
        console.error('Error checking founding member status:', err);
      }
    };
    checkFoundingMember();
  }, []);

  useEffect(() => {
    // Use ref to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Set a maximum timeout - if auth takes more than 30 seconds, something is wrong
    const maxTimeout = setTimeout(() => {
      console.error("[Auth] Authentication timed out after 30 seconds");
      setError("Authentication timed out. Please try again.");
      setTimeout(() => navigate("/login"), 2000);
    }, 30000);

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        console.log("[Auth] Processing callback, full URL:", window.location.href);
        console.log("[Auth] API endpoint:", API);
        setStatus("Extracting session...");
        
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          console.error("[Auth] No session_id found in URL hash");
          setError("No session ID received from authentication provider");
          clearTimeout(maxTimeout);
          setTimeout(() => navigate("/login"), 2000);
          return;
        }

        const sessionId = sessionIdMatch[1];
        console.log("[Auth] Session ID found, exchanging for token...");
        setStatus("Authenticating...");

        // Exchange session_id for session token
        // The backend sets an httpOnly cookie - we just need to include credentials
        let response;
        let lastError;
        
        // Check if this is a founding member Google signup
        const isFoundingMemberSignup = sessionStorage.getItem('founding_member_google_signup') === 'true';
        sessionStorage.removeItem('founding_member_google_signup'); // Clear the flag
        
        // Get UTM data if stored in sessionStorage
        let utmData = {};
        try {
          const storedUtm = sessionStorage.getItem('utm_data');
          if (storedUtm) {
            utmData = JSON.parse(storedUtm);
            sessionStorage.removeItem('utm_data'); // Clear after use
          }
        } catch (e) {
          console.warn('[Auth] Failed to parse UTM data:', e);
        }
        
        // Choose the appropriate endpoint
        const endpoint = isFoundingMemberSignup 
          ? `${API}/auth/founding-member-google-signup`
          : `${API}/auth/session`;
        
        console.log(`[Auth] Using endpoint: ${endpoint} (founding member: ${isFoundingMemberSignup})`);
        
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            console.log(`[Auth] Attempt ${attempt}/3 to ${endpoint}`);
            setStatus(`Authenticating... (attempt ${attempt}/3)`);
            
            // Build request body - include UTM data for founding member signups
            const requestBody = isFoundingMemberSignup
              ? { session_id: sessionId, ...utmData }
              : { session_id: sessionId };
            
            // Use fetch WITH credentials to receive and store httpOnly cookie
            const fetchResponse = await fetch(endpoint, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              credentials: 'include', // SECURITY: Include credentials to receive httpOnly cookie
              body: JSON.stringify(requestBody)
            });
            
            if (!fetchResponse.ok) {
              const errorData = await fetchResponse.json().catch(() => ({}));
              throw new Error(errorData.detail || `HTTP ${fetchResponse.status}`);
            }
            
            response = { data: await fetchResponse.json() };
            break; // Success, exit retry loop
          } catch (err) {
            lastError = err;
            console.warn(`[Auth] Attempt ${attempt} failed:`, err.message);
            if (attempt < 3) {
              await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
            }
          }
        }
        
        clearTimeout(maxTimeout);
        
        if (!response) {
          throw lastError || new Error("Failed after 3 attempts");
        }

        console.log("[Auth] Session exchange successful");
        setStatus("Success! Redirecting...");
        
        // Store session token in localStorage as backup for custom domains
        // where cookies may not work reliably
        if (response.data.session_token) {
          localStorage.setItem('session_token', response.data.session_token);
        }
        
        // Remove session_token from user data before storing (don't expose in user object)
        const userData = { ...response.data };
        delete userData.session_token;
        
        // Set user data
        setUser(userData);
        
        // Track Facebook Pixel - determine if new signup or returning login
        // New users won't have onboarding_complete set yet
        if (!userData.onboarding_complete) {
          trackSignup({ method: 'google' });
        } else {
          trackLogin({ method: 'google' });
        }
        
        // Also update localStorage immediately for cross-tab consistency
        localStorage.setItem('uncloned_user', JSON.stringify(userData));
        
        // Clear the hash from URL
        window.history.replaceState(null, "", window.location.pathname);
        
        // Small delay to ensure state is set before navigation
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Check if user needs onboarding
        // Use window.location for more reliable navigation after OAuth
        // CRITICAL: Check both onboarding_complete AND referral_source to capture marketing data
        // Skip for B2B users if their client has disabled onboarding
        const b2bOnboardingDisabled = userData.b2b_client_id && userData.client_features?.enable_onboarding === false;
        const needsOnboarding = !b2bOnboardingDisabled && (!userData.onboarding_complete || !userData.referral_source);
        
        if (needsOnboarding) {
          console.log("[Auth] Redirecting to onboarding (onboarding_complete:", userData.onboarding_complete, ", referral_source:", userData.referral_source, ")");
          window.location.href = "/onboarding";
        } else {
          console.log("[Auth] Redirecting to dashboard");
          window.location.href = "/dashboard";
        }
      } catch (error) {
        clearTimeout(maxTimeout);
        console.error("[Auth] Callback error:", error);
        const errorMsg = error.response?.data?.detail || error.message || "Authentication failed";
        
        // Special handling for new users trying to sign in without an account
        if (errorMsg === "NO_ACCOUNT_FOUND" || errorMsg.includes("NO_ACCOUNT_FOUND")) {
          setNoAccountFound(true);
          setError(null);
          return;
        }
        
        // Handle cancelled subscription - redirect to resubscribe page
        if (errorMsg === "SUBSCRIPTION_CANCELLED" || errorMsg.includes("SUBSCRIPTION_CANCELLED")) {
          window.location.href = "/resubscribe";
          return;
        }
        
        setError(errorMsg);
        toast.error("Authentication failed. Please try again.");
        setTimeout(() => navigate("/login"), 2000);
      }
    };

    processAuth();
    
    // Cleanup timeout on unmount
    return () => clearTimeout(maxTimeout);
  }, [navigate, setUser]);

  // No Account Found - Visual Screen
  if (noAccountFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#2D063E] to-[#4A0A66] p-4">
        <div className="max-w-md w-full">
          {/* Logo */}
          <div className="text-center mb-6">
            <img src="/new-logo-homepage.png" alt="UnCloned® Journal" className="h-12 mx-auto" />
          </div>
          
          {/* Card */}
          <div 
            className="rounded-3xl p-8 text-center"
            style={{
              background: 'linear-gradient(135deg, rgba(223, 18, 131, 0.1) 0%, rgba(0, 0, 0, 0.4) 100%)',
              border: '1px solid rgba(223, 18, 131, 0.3)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            }}
          >
            {/* Icon */}
            <div 
              className="w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, rgba(223, 18, 131, 0.2) 0%, rgba(255, 121, 1, 0.2) 100%)',
                border: '1px solid rgba(223, 18, 131, 0.3)',
              }}
            >
              <UserX className="w-10 h-10 text-[#df1283]" />
            </div>

            {/* Title */}
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              No Account Found
            </h1>

            {/* Message */}
            <p className="text-[#FDF2F8]/70 mb-6 leading-relaxed">
              We couldn't find an account with that Google email. 
              <span className="block mt-2 text-[#FDF2F8]/90 font-medium">
                Sign up to start your journaling journey!
              </span>
            </p>

            {/* Founding Member Highlight - only show if available */}
            {foundingMemberAvailable && (
            <div 
              className="rounded-xl p-4 mb-6"
              style={{
                background: 'linear-gradient(135deg, rgba(197, 165, 71, 0.15) 0%, rgba(255, 215, 0, 0.1) 100%)',
                border: '1px solid rgba(197, 165, 71, 0.3)',
              }}
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-[#FFD700]" />
                <span className="text-[#FFD700] font-semibold">Limited Time Offer</span>
              </div>
              <p className="text-[#FDF2F8]/80 text-sm">
                Become a <span className="text-[#FFD700] font-semibold">Founding Member</span> for free and get lifetime access to all premium features!
              </p>
            </div>
            )}

            {/* CTA Buttons */}
            <button
              onClick={() => window.location.href = '/#pricing'}
              className="w-full py-4 rounded-xl font-semibold text-white mb-3 transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #df1283 0%, #FF7901 100%)',
                boxShadow: '0 10px 30px rgba(223, 18, 131, 0.3)',
              }}
              data-testid="no-account-signup-btn"
            >
              View Plans & Sign Up
              <ArrowRight className="w-5 h-5" />
            </button>

            <button
              onClick={() => navigate('/login')}
              className="w-full py-3 rounded-xl text-[#FDF2F8]/70 hover:text-white transition-colors text-sm"
              data-testid="no-account-back-btn"
            >
              Back to Sign In
            </button>
          </div>

          {/* Help text */}
          <p className="text-center text-[#FDF2F8]/40 text-xs mt-6">
            Already have an account? Make sure you're using the same email you signed up with.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#2D063E] to-[#4A0A66]">
      <div className="text-center">
        {/* Logo */}
        <img src="/new-logo-homepage.png" alt="UnCloned® Journal" className="h-12 mx-auto mb-6" />
        
        {error ? (
          <>
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
              <X className="w-6 h-6 text-red-400" />
            </div>
            <p className="text-red-400 mb-2">Authentication Error</p>
            <p className="text-[#FDF2F8]/60 text-sm">{error}</p>
            <p className="text-[#FDF2F8]/40 text-xs mt-2">Redirecting to login...</p>
          </>
        ) : (
          <>
            <div className="loading-spinner mx-auto mb-4" />
            <p className="text-[#FDF2F8]">Signing you in...</p>
            <p className="text-[#FDF2F8]/50 text-xs mt-2">{status}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
